import React, { useEffect, useRef, useState } from 'react';
import api from '../../api';
import Loader from '../../components/Loader';
import CanvasJSReact from '@canvasjs/react-charts';
import { buildChartSeries } from './gatewaySamples';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

// `fromParent`: served from the 1h data the parent already fetched (no request).
// `aggregated`: fetch 15m aggregates (longer ranges); otherwise fetch raw EDAP samples.
const TIMEFRAMES = [
    { key: '1h', label: 'Last hour', hours: 1, fromParent: true, aggregated: false },
    { key: '24h', label: '24 hours', hours: 24, fromParent: false, aggregated: true },
    { key: '7d', label: '7 days', hours: 24 * 7, fromParent: false, aggregated: true },
    { key: '30d', label: '30 days', hours: 24 * 30, fromParent: false, aggregated: true },
];

// Keep CanvasJS snappy even if a raw range returns a lot of points.
const MAX_POINTS = 3000;

const downsample = samples => {
    if (samples.length <= MAX_POINTS) {
        return samples;
    }
    const step = Math.ceil(samples.length / MAX_POINTS);
    return samples.filter((_, i) => i % step === 0);
};

// "Last hour" reuses the 1h data the parent already fetched (also used to seed the live
// graph); the longer ranges fetch on demand here.
const GatewayTelemetry = ({ deviceId, recentSamples = [], recentSamplesReady = false }) => {
    const [timeframe, setTimeframe] = useState('1h');
    const [fetched, setFetched] = useState({ samples: [], isReady: false, error: false });

    const frame = TIMEFRAMES.find(t => t.key === timeframe) ?? TIMEFRAMES[0];

    useEffect(() => {
        if (frame.fromParent) {
            // Served from the parent's 1h data; nothing to fetch.
            return undefined;
        }

        let active = true;
        setFetched({ samples: [], isReady: false, error: false });

        const to = new Date();
        const from = new Date(to.getTime() - frame.hours * 60 * 60 * 1000);
        const request = frame.aggregated
            ? api.get_device_aggregated_samples(deviceId, from.toISOString(), to.toISOString())
            : api.get_device_samples(deviceId, from.toISOString(), to.toISOString());

        request
            .then(res => {
                if (active) {
                    setFetched({ samples: downsample(res?.data ?? []), isReady: true, error: false });
                }
            })
            .catch(() => {
                if (active) {
                    setFetched({ samples: [], isReady: true, error: true });
                }
            });

        return () => {
            active = false;
        };
    }, [deviceId, timeframe, frame.fromParent, frame.aggregated, frame.hours]);

    let samples;
    let isReady;
    let error;
    if (frame.fromParent) {
        samples = downsample(recentSamples);
        isReady = recentSamplesReady;
        error = false;
    } else {
        samples = fetched.samples;
        isReady = fetched.isReady;
        error = fetched.error;
    }

    // Remember legend toggles by series name so they survive re-renders / data refreshes
    // (options are rebuilt each render, which would otherwise reset visibility to the default).
    const visibilityRef = useRef({});

    const toggleDataSeries = e => {
        const visible = !(typeof e.dataSeries.visible === 'undefined' || e.dataSeries.visible);
        e.dataSeries.visible = visible;
        visibilityRef.current[e.dataSeries.name] = visible;
        e.chart.render();
    };

    const series = buildChartSeries(samples);
    series.forEach(s => {
        if (visibilityRef.current[s.name] !== undefined) {
            s.visible = visibilityRef.current[s.name];
        }
    });

    const options = {
        height: 320,
        zoomEnabled: true,
        animationEnabled: false,
        axisX: { valueFormatString: 'MMM DD HH:mm' },
        axisY: {
            lineThickness: 1,
            // Highlight the zero line so export/charging (negative) reads clearly.
            stripLines: [{ value: 0, color: '#aab1bb', thickness: 1 }],
        },
        legend: { cursor: 'pointer', itemclick: toggleDataSeries },
        toolTip: { shared: true },
        data: series,
    };

    return (
        <div className="gateway-telemetry">
            <div className="gateway-telemetry__controls">
                {TIMEFRAMES.map(frame => (
                    <button
                        key={frame.key}
                        type="button"
                        className={`gateway-telemetry__preset ${timeframe === frame.key ? 'gateway-telemetry__preset--active' : ''}`}
                        onClick={() => setTimeframe(frame.key)}
                    >
                        {frame.label}
                    </button>
                ))}
            </div>

            {!isReady ? (
                <Loader />
            ) : error ? (
                <p className="gateway-section__empty">Could not load telemetry for this gateway.</p>
            ) : samples.length === 0 ? (
                <p className="gateway-section__empty">No telemetry in this period.</p>
            ) : (
                <CanvasJSChart options={options} />
            )}
        </div>
    );
};

// Memoized so live SSE samples (which re-render the parent every interval) don't re-render
// the historical chart — that was rebuilding its options and resetting the legend toggles.
// It only re-renders when its own props (deviceId, the 24h data) actually change.
export default React.memo(GatewayTelemetry);
